import { BaseModel, BaseWithId, HasId } from '@app/shared/models/base';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslationEntry } from '@app/shared/models/translatable';
import * as _ from 'lodash-es';
import { ActionListItem } from '@app/shared/components/action-list/action-list.component';
import { DragNDropChipable } from '@app/shared/components/drag-n-drop-chips/drag-n-drop-chips.component';

export enum CaseNumberStructureLevel {
    CLIENT = 'CLIENT',
    DEPARTMENT = 'DEPARTMENT',
    OFFICE = 'OFFICE',
}

export class CaseNumberStructureLevels {

    public static ALL: TranslationEntry[] = [
        { key: CaseNumberStructureLevel.OFFICE, trans: marker('case_number_management.case_number_structure_level.office') },
        { key: CaseNumberStructureLevel.DEPARTMENT, trans: marker('case_number_management.case_number_structure_level.department') },
        { key: CaseNumberStructureLevel.CLIENT, trans: marker('case_number_management.case_number_structure_level.client') }
    ];

    public static getByKey(key: string): TranslationEntry {
        key = typeof key === 'string' ? key.toUpperCase() : key;
        const found = _.find(CaseNumberStructureLevels.ALL, (type: TranslationEntry) => type.key === key);
        return found || { key: null, trans: '' };
    }

    public static get CLIENT() {
        return CaseNumberStructureLevels.getByKey(CaseNumberStructureLevel.CLIENT);
    }

    public static get DEPARTMENT() {
        return CaseNumberStructureLevels.getByKey(CaseNumberStructureLevel.DEPARTMENT);
    }

    public static get OFFICE() {
        return CaseNumberStructureLevels.getByKey(CaseNumberStructureLevel.OFFICE);
    }
}

export enum CaseNumberStructureDataType {
    INT = 'INT',
    DATE = 'DATE',
    TEXT = 'TEXT',
}

export enum FieldCode {
    CONSECUTIVE_NUMBER = 'field.consecutive_number',
    YEAR = 'field.year.yy',
    MONTH = 'field.month.mm',
    DAY = 'field.day.dd',
    OFFICE = 'field.office',
    ASSESSOR_CODE = 'field.assessor_code',
    CASE_FREE_1 = 'field.case_free1',
    CASE_FREE_2 = 'field.case_free2',
    SEPARATOR_HYPHEN = 'field.separator.hyphen',
    SEPARATOR_SLASH = 'field.separator.slash',
    SEPARATOR_UNDERLINE = 'field.separator.underline'
}

export class FieldCodes {

    public static ALL: TranslationEntry[] = [
        { key: FieldCode.CONSECUTIVE_NUMBER, trans: marker('case_number_management.field_code.consecutive_number') },
        { key: FieldCode.YEAR, trans: marker('case_number_management.field_code.year'), trans2: marker('case_number_management.field_code.year_B') },
        { key: FieldCode.MONTH, trans: marker('case_number_management.field_code.month'), trans2: marker('case_number_management.field_code.month_B') },
        { key: FieldCode.DAY, trans: marker('case_number_management.field_code.day'), trans2: marker('case_number_management.field_code.day_B') },
        { key: FieldCode.OFFICE, trans: marker('case_number_management.field_code.office'), trans2: marker('case_number_management.field_code.office_B') },
        { key: FieldCode.ASSESSOR_CODE, trans: marker('case_number_management.field_code.assessor_code'), trans2: marker('case_number_management.field_code.assessor_code_B') },
        { key: FieldCode.CASE_FREE_1, trans: marker('case_number_management.field_code.case_free_1'), trans2: marker('case_number_management.field_code.case_free_1_B') },
        { key: FieldCode.CASE_FREE_2, trans: marker('case_number_management.field_code.case_free_2'), trans2: marker('case_number_management.field_code.case_free_2_B') },
        { key: FieldCode.SEPARATOR_HYPHEN, trans: '-', trans2: '-' },
        { key: FieldCode.SEPARATOR_SLASH, trans: '/', trans2: '/' },
        { key: FieldCode.SEPARATOR_UNDERLINE, trans: '_', trans2: '_' }
    ];

    public static getByKey(key: string): TranslationEntry {
        key = typeof key === 'string' ? key.toLowerCase() : key;
        const found = _.find(FieldCodes.ALL, (type: TranslationEntry) => type.key === key);
        return found || { key: null, trans: '' };
    }

    public static get CONSECUTIVE_NUMBER() {
        return FieldCodes.getByKey(FieldCode.CONSECUTIVE_NUMBER);
    }

    public static get YEAR() {
        return FieldCodes.getByKey(FieldCode.YEAR);
    }

    public static get MONTH() {
        return FieldCodes.getByKey(FieldCode.MONTH);
    }

    public static get DAY() {
        return FieldCodes.getByKey(FieldCode.DAY);
    }

    public static get OFFICE() {
        return FieldCodes.getByKey(FieldCode.OFFICE);
    }

    public static get ASSESSOR_CODE() {
        return FieldCodes.getByKey(FieldCode.ASSESSOR_CODE);
    }

    public static get CASE_FREE_1() {
        return FieldCodes.getByKey(FieldCode.CASE_FREE_1);
    }

    public static get CASE_FREE_2() {
        return FieldCodes.getByKey(FieldCode.CASE_FREE_2);
    }

    public static get SEPARATOR_HYPHEN() {
        return FieldCodes.getByKey(FieldCode.SEPARATOR_HYPHEN);
    }

    public static get SEPARATOR_SLASH() {
        return FieldCodes.getByKey(FieldCode.SEPARATOR_SLASH);
    }

    public static get SEPARATOR_UNDERLINE() {
        return FieldCodes.getByKey(FieldCode.SEPARATOR_UNDERLINE);
    }
}

export enum CounterResetMethod {
    NEVER = 'NEVER',
    ANNUAL = 'ANNUAL'
}

export class CounterResetMethods {

    public static ALL: TranslationEntry[] = [
        { key: CounterResetMethod.NEVER, trans: marker('case_number_management.counter_reset_method.never') },
        { key: CounterResetMethod.ANNUAL, trans: marker('case_number_management.counter_reset_method.annual') }
    ];

    public static getByKey(key: string): TranslationEntry {
        key = typeof key === 'string' ? key.toUpperCase() : key;
        const found = _.find(CounterResetMethods.ALL, (type: TranslationEntry) => type.key === key);
        return found || { key: null, trans: '' };
    }

    public static get NEVER() {
        return CounterResetMethods.getByKey(CounterResetMethod.NEVER);
    }

    public static get ANNUAL() {
        return CounterResetMethods.getByKey(CounterResetMethod.ANNUAL);
    }
}

export enum GenerationMethod {
    MANUAL = 'MANUAL',
    AUTOMATIC = 'AUTOMATIC'
}

export class GenerationMethods {

    public static ALL: TranslationEntry[] = [
        { key: GenerationMethod.MANUAL, trans: marker('case_number_management.generation_methods.manual') },
        { key: GenerationMethod.AUTOMATIC, trans: marker('case_number_management.generation_methods.automatic') }
    ];

    public static getByKey(key: string): TranslationEntry {
        key = typeof key === 'string' ? key.toUpperCase() : key;
        const found = _.find(GenerationMethods.ALL, (type: TranslationEntry) => type.key === key);
        return found || { key: null, trans: '' };
    }

    public static get MANUAL() {
        return GenerationMethods.getByKey(GenerationMethod.MANUAL);
    }

    public static get AUTOMATIC() {
        return GenerationMethods.getByKey(GenerationMethod.AUTOMATIC);
    }
}

export class CaseNoField extends BaseWithId
                         implements DragNDropChipable {
    public data_type: CaseNumberStructureDataType;
    public default_value: string;
    public format: string;
    public identifier_code: FieldCode;
    public identifier_value: string;
    public length: number;
    public multiple: boolean;
    public required: boolean;
    public sort_order: number;

    get identifierCodeAsTranslatable() {
        return FieldCodes.getByKey(this.identifier_code);
    }

    get displayName() {
        return this.identifierCodeAsTranslatable.trans;
    }

    get isSeparator(): boolean {
        return this.identifier_code === FieldCode.SEPARATOR_HYPHEN ||
               this.identifier_code === FieldCode.SEPARATOR_SLASH ||
               this.identifier_code === FieldCode.SEPARATOR_UNDERLINE;
    }

    get notRemovable(): boolean {
        return this.required;
    }

    constructor(json?: any) {
        super(json);
    }

}

export class CaseNoStructure extends BaseModel implements ActionListItem {
    public counter_last_reset: number;
    public counter_next_value: number;
    public counter_reset_method: CounterResetMethod;
    public counter_start: number;
    public fields: CaseNoField[];
    public generation_method: GenerationMethod;
    public reference_id: string;
    public reference_name: string;
    public settings_base_level: CaseNumberStructureLevel;

    public readonly trans = {
        counter_length: marker('case_number_structure.counter_length'),
        counter_next_value: marker('case_number_structure.counter_next_value'),
        counter_reset_method: marker('case_number_structure.counter_reset_method'),
        counter_start: marker('case_number_structure.counter_start'),
        generation_method: marker('case_number_structure.generation_method')
    };

    get actionListDisplayName(): string {
        return this.reference_name;
    }

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            fields: json && json.fields ? json.fields.map(field => new CaseNoField(field)) : [],
        });
    }

    public getField(fieldCode: FieldCode): CaseNoField {
        return this.fields.find(field => field.identifier_code === fieldCode);
    }

}

export class CaseNoSettings extends BaseModel
                            implements HasId {
    get id() {
        return this.client_structure ? this.client_structure.reference_id : null;
    }

    get clientStructureAsList(): CaseNoStructure[] {
        return [this.client_structure];
    }

    public available_fields: CaseNoField[];
    public available_levels: CaseNumberStructureLevel[];
    public base_level: CaseNumberStructureLevel;
    public client_structure: CaseNoStructure;
    public department_structures: CaseNoStructure[];
    public office_structures: CaseNoStructure[];

    public readonly trans = {

    };

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            available_fields: json && json.available_fields ? json.available_fields.map(field => new CaseNoField(field)) : [],
            client_structure: new CaseNoStructure(json && json.client_structure),
            department_structures: json && json.department_structures ? json.department_structures.map(structure => new CaseNoStructure(structure)) : [],
            office_structures: json && json.office_structures ? json.office_structures.map(structure => new CaseNoStructure(structure)) : []
        });
    }

    public getStructureListByLevel(level: CaseNumberStructureLevel): CaseNoStructure[] {
        switch (level) {
            case CaseNumberStructureLevel.CLIENT: {
                return this.clientStructureAsList;
            }
            case CaseNumberStructureLevel.DEPARTMENT: {
                return this.department_structures;
            }
            case CaseNumberStructureLevel.OFFICE: {
                return this.office_structures;
            }
            default: {
                return [];
            }
        }
    }

    public getStructure(level: CaseNumberStructureLevel, reference_id: string): CaseNoStructure {
        return this.getStructureListByLevel(level).find(structure => structure.reference_id === reference_id);
    }

    public setStructure(level: CaseNumberStructureLevel, reference_id: string, structure: CaseNoStructure): void {
        if (level === CaseNumberStructureLevel.CLIENT) {
            this.client_structure = structure;
        } else {
            const list = this.getStructureListByLevel(level);
            const structureToReplace = list.find(toReplace => toReplace.reference_id === structure.reference_id);
            const idx = list.indexOf(structureToReplace);
            list[idx] = structure;
        }
    }

}
